/* eslint-env browser */
import React from 'react';
import moment from 'moment/min/moment-with-locales';
import { colors } from "../components/Theme/WebTheme";
import {
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Tooltip,
  Chip,
} from '@mui/material';
import { useTranslation } from "react-i18next";
import OtherPerson from 'components/OtherPerson';
import { getLangKey } from 'common/src/other/getLangKey';
import ClickableUserLink from '../components/ClickableUserLink';
import ClickableBookingLink from '../components/ClickableBookingLink';
import { printDocument, formatAddisDateTime } from './exportUtils';


export const calcEst = false;
export const showEst = true;
export const optionsRequired = false;


export const MAIN_COLOR = colors.TAXIPRIMARY;
export const SECONDORY_COLOR = colors.TAXISECONDORY;

export const FONT_FAMILY = '"Roboto", "Helvetica", "Arial", sans-serif';

// ---------------------------------------------------------------------------
// Booking source / type classification
// ---------------------------------------------------------------------------
// Bookings are created from several distinct entry points (mobile app, web
// dashboard by a call-center/admin user, street pickup, donkey meter, offline
// meter) but historically the source was only inferable from a scatter of
// booleans (booking_from_web, manualRequest, streetPickup, booking_type, …).
// getBookingType() folds those EXISTING fields into one stable key so the admin
// can finally see, per booking, where it came from. It derives at read time —
// no schema change, no migration, and it classifies every historical booking.
//
// Order matters: the online donkey-meter ride carries streetPickup:true, so it
// must be matched before the street-pickup branch; the offline meter carries
// booking_type:'OFFLINE_METER', matched first of all.
export const BOOKING_TYPE = {
  APP: 'app',
  CALL_CENTER: 'call_center',
  ADMIN: 'admin',
  STREET_PICKUP: 'street_pickup',
  DONKEY_METER: 'donkey_meter',
  OFFLINE: 'offline',
};

export const getBookingType = (booking) => {
  if (!booking) return BOOKING_TYPE.APP;

  // Offline meter ride — created without connectivity, synced later.
  if (
    booking.booking_type === 'OFFLINE_METER' ||
    (booking.offline_metadata && booking.offline_metadata.offline_id) ||
    (typeof booking.status === 'string' && booking.status.indexOf('OFFLINE') === 0)
  ) {
    return BOOKING_TYPE.OFFLINE;
  }

  // Donkey meter — online metered ride (pay-as-you-go). NOTE: also sets
  // streetPickup:true, so it must be matched before the street-pickup branch.
  if (booking.booking_type === 'donkey_meter' || booking.donkey_meter_metadata) {
    return BOOKING_TYPE.DONKEY_METER;
  }

  // Call center — booked by a call-center agent/supervisor from the dashboard.
  if (
    booking.booking_type_call_center ||
    booking.callcenteragent ||
    booking.callcentersupervisor
  ) {
    return BOOKING_TYPE.CALL_CENTER;
  }

  // Admin / fleet — other dashboard-created bookings (web form, not call center).
  if (
    booking.booking_type_admin ||
    booking.booking_type_fleetadmin ||
    booking.booking_from_web
  ) {
    return BOOKING_TYPE.ADMIN;
  }

  // Street pickup — driver or customer hailed on the street (incl. shared trips).
  if (
    booking.streetPickup ||
    booking.manualRequest ||
    booking.bookingType === 'shared_trip'
  ) {
    return BOOKING_TYPE.STREET_PICKUP;
  }

  // Default — regular customer booking through the mobile app.
  return BOOKING_TYPE.APP;
};

// Display metadata per booking type: i18n key, English fallback label, and a
// chip colour. Centralised so the BookingHistory column and the Dashboard
// filter/legend stay in lock-step.
export const bookingTypeMeta = {
  [BOOKING_TYPE.APP]:           { key: 'booking_type_app',          label: 'App',           color: '#2196F3' },
  [BOOKING_TYPE.CALL_CENTER]:   { key: 'booking_type_call_center',  label: 'Call Center',   color: colors.VIOLET },
  [BOOKING_TYPE.ADMIN]:         { key: 'booking_type_admin',        label: 'Portal / Web',   color: '#00BCD4' },
  [BOOKING_TYPE.STREET_PICKUP]: { key: 'booking_type_street',       label: 'Street Pickup', color: '#FF9800' },
  [BOOKING_TYPE.DONKEY_METER]:  { key: 'booking_type_meter',        label: 'Donkey Meter',  color: colors.GREEN },
  [BOOKING_TYPE.OFFLINE]:       { key: 'booking_type_offline',      label: 'Offline',       color: '#795548' },
};

// Stable, display-ordered list of type keys — drives the dashboard filter.
export const BOOKING_TYPE_ORDER = [
  BOOKING_TYPE.APP,
  BOOKING_TYPE.CALL_CENTER,
  BOOKING_TYPE.ADMIN,
  BOOKING_TYPE.STREET_PICKUP,
  BOOKING_TYPE.DONKEY_METER,
  BOOKING_TYPE.OFFLINE,
];

// Localised label for a type key, with graceful fallback when the translation
// key isn't present in the loaded language bundle (resources load from Firebase
// at runtime, so keys may be absent — mirror the t('x')==='x' idiom used app-wide).
export const bookingTypeLabelFor = (typeKey, t) => {
  const meta = bookingTypeMeta[typeKey] || bookingTypeMeta[BOOKING_TYPE.APP];
  if (!t) return meta.label;
  return t(meta.key, { defaultValue: meta.label });
};

// Localised label for a booking (resolves its type, then labels it).
export const getBookingTypeLabel = (booking, t) => bookingTypeLabelFor(getBookingType(booking), t);

export const bookingHistoryColumns = (role, settings, t, isRTL) => [
  { title: t('booking_date'), field: 'bookingDate', defaultSort:'desc', render: rowData => rowData.bookingDate?moment(rowData.bookingDate).format('DD-MMM-YYYY, hh:mm A'):null},
  { title: t('booking_id'), field: 'id' },
  { 
    title: t('booking_ref'), 
    field: 'reference',
    render: rowData => rowData.reference ? (
      <ClickableBookingLink 
        value={rowData.reference}
        searchBy="reference"
        bookingId={rowData.id}
        iconType="receipt"
      />
    ) : '-'
  },
  { title: t('car_type'), field: 'carType', render: rowData => rowData.carType ? t(getLangKey(rowData.carType)) : null },
  { 
    title: t('assign_driver'), 
    field: 'driver_name',
    render: rowData => rowData.driver_name ? (
      <ClickableUserLink 
        value={rowData.driver_name}
        type="driver"
        searchField="name"
        userId={rowData.driver_id}
        iconType="car"
      />
    ) : '-'
  },
  {
    title: t('customer_name'),
    field: 'customer_name',
    render: rowData => {
      // Shared trips have N passengers, not a single customer — clicking the
      // name shouldn't open a customer profile. Route to the booking detail
      // (same target as the reference column) so the user lands on the place
      // that actually shows the passenger list.
      if (rowData.bookingType === 'shared_trip' && rowData.customer_name) {
        return (
          <ClickableBookingLink
            value={rowData.customer_name}
            searchBy="reference"
            bookingId={rowData.id}
            iconType="person"
          />
        );
      }
      return rowData.customer_name ? (
        <ClickableUserLink
          value={rowData.customer_name}
          type="customer"
          searchField="name"
          userId={rowData.customer_id}
          iconType="person"
        />
      ) : '-';
    }
  },
  {
    title: t('customer_contact'),
    field: 'customer_contact',
    render: rowData => {
      // Shared trips have N passengers, not a single contact — render a
      // "Shared (N pax)" chip with the per-passenger phone list in the
      // tooltip instead of trying to show one phone number.
      if (rowData.bookingType === 'shared_trip' && Array.isArray(rowData.passengers) && rowData.passengers.length > 0) {
        const tooltipText = rowData.passengers
          .map(p => `${(p.firstName || '') + (p.lastName ? ' ' + p.lastName : '')} — ${p.phone || '-'}`)
          .join('\n');
        return (
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>} arrow placement="top">
            <Chip
              label={t('shared_trip_chip', {
                count: rowData.passengers.length,
                defaultValue: 'Shared ({{count}} customers)',
                defaultValue_one: 'Shared ({{count}} customer)',
              })}
              size="small"
              sx={{ backgroundColor: colors.YELLOW, color: colors.BLACK, fontWeight: 'bold' }}
            />
          </Tooltip>
        );
      }
      return rowData.customer_contact ? (
        <ClickableUserLink
          value={rowData.customer_contact}
          type="customer"
          searchField="phone"
          userId={rowData.customer_id}
          iconType="phone"
        />
      ) : '-';
    }
  },
  {
    title: t('booking_status_web'), field: 'status',
    render: rowData =>
      <div
        style={{ backgroundColor: rowData.status === "CANCELLED" ? colors.RED : rowData.status === "COMPLETE" ? colors.GREEN : colors.YELLOW, color: "white", padding: 7, borderRadius: "15px", fontWeight: "bold", width: "150px", margin: 'auto' }}
      >{t(rowData.status)}</div>,
  },
  {
    // Where the booking came from (call center, street pickup, app, donkey
    // meter, offline…). Derived from existing source fields — see getBookingType().
    title: t('booking_type_col', { defaultValue: 'Booking Type' }),
    field: 'bookingTypeLabel',
    customSort: (a, b) => getBookingTypeLabel(a, t).localeCompare(getBookingTypeLabel(b, t)),
    customFilterAndSearch: (term, rowData) =>
      getBookingTypeLabel(rowData, t).toLowerCase().includes((term || '').toLowerCase()),
    render: rowData => {
      const meta = bookingTypeMeta[getBookingType(rowData)] || bookingTypeMeta[BOOKING_TYPE.APP];
      return (
        <Chip
          label={getBookingTypeLabel(rowData, t)}
          size="small"
          style={{ backgroundColor: meta.color, color: colors.WHITE, fontWeight: 'bold' }}
        />
      );
    },
  },
  { title: t('otp'), field: 'otp', render: rowData => <div>{rowData.otp ? rowData.otp : t("false")}</div> },
  {
    title: t('estimate', { defaultValue: 'Estimate' }),
    field: 'estimate',
    render: (rowData) =>
      rowData.estimate
        ? settings.swipe_symbol
          ? rowData.estimate + " " + settings.symbol
          : settings.symbol + " " + rowData.estimate
        : settings.swipe_symbol
          ? "0 " + settings.symbol
          : settings.symbol + " 0",
  },
  {
    title: t('trip_cost'),
    field: 'trip_cost',
    render: (rowData) => {
      const tripCost = parseFloat(rowData.trip_cost) || 0;
      const estimate = parseFloat(rowData.estimate) || 0;
      // Both estimateTime and total_trip_time are in SECONDS
      const estimateTimeInSec = typeof rowData.estimateTime === 'string' 
        ? parseFloat(rowData.estimateTime) 
        : parseFloat(rowData.estimateTime) || 0;
      
      const totalTripTimeInSec = parseFloat(rowData.total_trip_time) || 0;
      
      const estimateDistance = parseFloat(rowData.estimateDistance) || 0;
      const actualDistance = parseFloat(rowData.distance) || 0;
      
      // Convert to minutes for display
      const estimateTime = estimateTimeInSec / 60;
      const totalTripTime = totalTripTimeInSec / 60;
      
      // Check if it's a valid trip (different pickup and drop addresses)
      const isValidTrip = rowData.pickupAddress !== rowData.dropAddress;
      
      // Calculate multipliers
      let priceMultiplier = 1;
      let timeMultiplier = 1;
      let distanceMultiplier = 1;
      let backgroundColor = colors.GREEN;
      let confidenceLevel = 'normal';
      let isUnderpriced = false;
      
      if (estimate > 0) {
        priceMultiplier = tripCost / estimate;
        // Check if cost is significantly less than estimated
        if (priceMultiplier < 0.7) {
          isUnderpriced = true;
        }
      }
      
      if (estimateTimeInSec > 0) {
        timeMultiplier = totalTripTimeInSec / estimateTimeInSec;
      }
      
      if (estimateDistance > 0) {
        distanceMultiplier = actualDistance / estimateDistance;
      }
      
      // Advanced spike detection algorithm
      // Combines price, time, and distance variance for maximum accuracy
      const calculateSuspicionScore = () => {
        let score = 0;
        let factors = [];
        
        // Price factor (0-40 points, reduced from 50 to make room for distance)
        if (priceMultiplier >= 3) {
          score += 40;
          factors.push('Extreme price increase');
        } else if (priceMultiplier >= 2) {
          score += 30;
          factors.push('High price increase');
        } else if (priceMultiplier >= 1.5) {
          score += 20;
          factors.push('Moderate price increase');
        } else if (priceMultiplier >= 1.3) {
          score += 10;
          factors.push('Slight price increase');
        }
        
        // Time factor (0-25 points)
        // If trip took significantly less time but costs more, it's suspicious
        if (estimateTimeInSec > 0) {
          if (timeMultiplier < 0.8 && priceMultiplier > 1.3) {
            score += 25;
            factors.push('Shorter trip, higher cost');
          } else if (timeMultiplier > 1.5 && priceMultiplier > 1.5) {
            // Longer trip with proportional price increase is more acceptable
            score -= 10;
            factors.push('Longer trip justifies cost');
          } else if (timeMultiplier < 1.2 && priceMultiplier > 2) {
            score += 20;
            factors.push('Normal time, excessive cost');
          }
        }
        
        // Distance factor (0-25 points) - Only apply if trip is valid
        if (isValidTrip && estimateDistance > 0) {
          // Excessive distance (possible route manipulation)
          if (distanceMultiplier > 1.5) {
            score += 25;
            factors.push('Excessive distance traveled');
          } else if (distanceMultiplier > 1.3) {
            score += 15;
            factors.push('Longer route taken');
          }
          
          // Distance-price mismatch
          if (distanceMultiplier > 1.3 && priceMultiplier > 1.5) {
            score += 10;
            factors.push('Long route + high price');
          } else if (distanceMultiplier < 1.1 && priceMultiplier > 1.5) {
            score += 15;
            factors.push('Normal distance, high price');
          }
          
          // Check if distance increase justifies price
          if (distanceMultiplier > 1.2 && Math.abs(priceMultiplier - distanceMultiplier) < 0.2) {
            score -= 10;
            factors.push('Price matches distance');
          }
        }
        
        // Multi-factor correlation (0-10 points)
        // Check consistency across all three metrics
        if (estimateDistance > 0 && estimateTimeInSec > 0 && isValidTrip) {
          const avgMultiplier = (priceMultiplier + timeMultiplier + distanceMultiplier) / 3;
          const variance = Math.abs(priceMultiplier - avgMultiplier) + 
                          Math.abs(timeMultiplier - avgMultiplier) + 
                          Math.abs(distanceMultiplier - avgMultiplier);
          
          if (variance > 2) {
            score += 10;
            factors.push('Inconsistent metrics');
          } else if (variance < 0.5) {
            score -= 5;
            factors.push('Consistent pricing');
          }
        } else if (estimateTimeInSec > 0) {
          // Fallback to price-time correlation if distance not available
          const correlation = Math.abs(priceMultiplier - timeMultiplier);
          if (correlation > 1.5) {
            score += 10;
            factors.push('Price-time mismatch');
          } else if (correlation < 0.3) {
            score -= 5;
            factors.push('Proportional pricing');
          }
        }
        
        return { score: Math.max(0, Math.min(100, score)), factors };
      };
      
      const { score, factors } = calculateSuspicionScore();
      
      // Determine color and confidence level based on score
      if (isUnderpriced) {
        // Special case: significantly underpriced
        backgroundColor = '#2196F3'; // Blue for underpriced
        confidenceLevel = 'underpriced';
      } else if (score >= 70) {
        backgroundColor = colors.RED;
        confidenceLevel = 'critical';
      } else if (score >= 50) {
        backgroundColor = '#FF9800';
        confidenceLevel = 'warning';
      } else if (score >= 30) {
        backgroundColor = colors.YELLOW;
        confidenceLevel = 'caution';
      } else {
        backgroundColor = colors.GREEN;
        confidenceLevel = 'normal';
      }
      
      const formattedCost = tripCost > 0
        ? settings.swipe_symbol
          ? tripCost + " " + settings.symbol
          : settings.symbol + " " + tripCost
        : settings.swipe_symbol
          ? "0 " + settings.symbol
          : settings.symbol + " 0";
      
      // Enhanced tooltip with detailed analysis
      const tooltipContent = () => {
        if (estimate > 0 || estimateTimeInSec > 0 || estimateDistance > 0) {
          let tooltip = [];
          
          if (estimate > 0) {
            tooltip.push(`Price: ${priceMultiplier.toFixed(1)}x estimate (${settings.swipe_symbol ? estimate + " " + settings.symbol : settings.symbol + " " + estimate})`);
          }
          
          if (estimateTimeInSec > 0) {
            tooltip.push(`Time: ${timeMultiplier.toFixed(1)}x estimate (${estimateTime.toFixed(1)} min → ${totalTripTime.toFixed(1)} min)`);
          }
          
          if (isValidTrip && estimateDistance > 0) {
            tooltip.push(`Distance: ${distanceMultiplier.toFixed(1)}x estimate (${estimateDistance.toFixed(1)} km → ${actualDistance.toFixed(1)} km)`);
          }
          
          if (isUnderpriced) {
            tooltip.push(`⚠️ Significantly underpriced (${Math.round(priceMultiplier * 100)}% of estimate)`);
            tooltip.push(`Driver may have given a discount`);
          } else if (score >= 30) {
            tooltip.push(`Confidence: ${Math.round(score)}% suspicious`);
            if (factors.length > 0) {
              tooltip.push(`Factors: ${factors.join(', ')}`);
            }
          }
          
          return tooltip.join('\n');
        }
        return '';
      };
      
      // Check if it's an offline booking
      const isOffline = rowData.offline_metadata && rowData.offline_metadata.offline_id;
      const offlineTooltip = isOffline 
        ? `Offline Meter Trip - ${rowData.offline_metadata.offline_id || ''}\n`
        : '';
      
      return (
        <Tooltip title={`${offlineTooltip}${tooltipContent()}`} arrow placement="top">
          <div
            style={{
              backgroundColor: backgroundColor,
              color: "white",
              padding: 7,
              borderRadius: "15px",
              fontWeight: "bold",
              width: "150px",
              margin: 'auto',
              cursor: (estimate > 0 || estimateTimeInSec > 0) ? 'help' : 'default',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px'
            }}
          >
            {isOffline && (
              <span style={{ fontSize: '16px', marginRight: '3px' }}>⏱️</span>
            )}
            {formattedCost}
            {(score >= 50 || isUnderpriced) && (
              <div style={{
                position: 'absolute',
                top: -5,
                right: -5,
                backgroundColor: isUnderpriced ? '#2196F3' : colors.RED,
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                border: '2px solid white'
              }}>
                !
              </div>
            )}
          </div>
        </Tooltip>
      );
    },
  },
];

export const BookingModalBody = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { classes, handleChange, auth, profileData, instructionData, otherPerson, setOtherPerson } = props;
  return (
    <span>
      {auth.profile.usertype === 'customer' && !auth.profile.firstName ?
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            margin="normal"
            required={auth.profile.firstName ? false : true}
            fullWidth
            id="firstName"
            label={t('firstname')}
            name="firstName"
            autoComplete="firstName"
            onChange={handleChange}
            value={profileData.firstName}
            autoFocus
            className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}
            style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
          />
        </Grid>
        : null}
      {auth.profile.usertype === 'customer' && !auth.profile.lastName ?
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            margin="normal"
            required={auth.profile.lastName ? false : true}
            fullWidth
            id="lastName"
            label={t('lastname')}
            name="lastName"
            autoComplete="lastName"
            onChange={handleChange}
            value={profileData.lastName}
            className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}
            style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
          />
        </Grid>
        : null}
      {auth.profile.usertype === 'customer' && !auth.profile.email ?
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            margin="normal"
            required={auth.profile.email ? false : true}
            fullWidth
            id="email"
            label={t('email')}
            name="email"
            autoComplete="email"
            onChange={handleChange}
            value={profileData.email}
            className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}
            style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
          />
        </Grid>
        : null}
      <OtherPerson
        classes={classes}
        otherPerson={otherPerson}
        handleChange={handleChange}
        setOtherPerson={setOtherPerson}
        instructionData={instructionData}
      />
      <Typography component="h2" variant="h5" style={{ marginTop: 15, color: colors.BLACK }}>
        {t('estimate_fare_text')}
      </Typography>
    </span>
  )
}

export const validateBookingObj = (t, bookingObject, instructionData) => {
  delete bookingObject.driverEstimates;
  return { bookingObject };
}

export const PanicSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { classes, data, handleTextChange } = props;
  return (
    <span>
      <Typography component="h1" variant="h5" style={{ marginTop: '15px', textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
        {t('panic_num')}
      </Typography>
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        id="panic"
        label={t('panic_num')}
        className={isRTL === "rtl" ? [classes.rootRtl_1, classes.right] : classes.textField}
        name="panic"
        autoComplete="panic"
        onChange={handleTextChange}
        value={data.panic}
      />
    </span>
  )
}

export const DispatchSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { autoDispatch, onChange } = props;
  return (
    <FormControlLabel
      style={{ flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
      control={
        <Switch
          checked={autoDispatch}
          onChange={onChange}
          name="autoDispatch"
          color="primary"
        />
      }
      label={<Typography>{t('auto_dispatch')}</Typography>}
    />
  )
}

export const BookingImageSettings = (props) => {
  return null;
}

export const carTypeColumns = (t, isRTL, onClick) =>  [
  { title: t('name'), field: 'name',render: rowData => rowData.name? <Typography>{t(getLangKey(rowData.name))}</Typography>:null
  },
  {
    title: t('image'), field: 'image',
    initialEditValue: 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png',
    render: rowData => rowData.image ? <button onClick={() => { onClick(rowData) }}><img alt={t('car_image')} src={rowData.image} style={{ width: 50 }} /></button> : null
  },
    { title: t('base_fare'), field: 'base_fare', type: 'numeric',  initialEditValue: 0 },
    { title: t('rate_per_unit_distance'), field: 'rate_per_unit_distance', type: 'numeric',  initialEditValue: 0},
    { title: t('rate_per_hour'), field: 'rate_per_hour', type: 'numeric',  initialEditValue: 0},
    { title: t('min_fare'), field: 'min_fare', type: 'numeric',  initialEditValue: 0},
    { title: t('convenience_fee'), field: 'convenience_fees', type: 'numeric',  initialEditValue: 0},
  {
    title: t('convenience_fee_type'),
    field: 'convenience_fee_type',
    lookup: { flat: t('flat'), percentage: t('percentage') },
  },
  // Meter-specific rate overrides used by Donkey Meter + Offline Meter
  // (OfflineFareCalculator). When set, they replace the regular rate
  // for meter-only ride types so meter pricing can be tuned without
  // affecting dispatched bookings. When unset (or 0), the meter falls
  // back to the regular rate above — existing car types stay correct
  // without any admin action.
    { title: t('base_fare_meter'), field: 'base_fare_meter', type: 'numeric',  initialEditValue: 0 },
    { title: t('rate_per_unit_distance_meter'), field: 'rate_per_unit_distance_meter', type: 'numeric',  initialEditValue: 0},
    { title: t('rate_per_hour_meter'), field: 'rate_per_hour_meter', type: 'numeric',  initialEditValue: 0},
    { title: t('min_fare_meter'), field: 'min_fare_meter', type: 'numeric',  initialEditValue: 0},
    { title: t('convenience_fee_meter'), field: 'convenience_fees_meter', type: 'numeric',  initialEditValue: 0},
  {
    title: t('convenience_fee_type_meter'),
    field: 'convenience_fee_type_meter',
    lookup: { flat: t('flat'), percentage: t('percentage') },
  },
    { title: t('rate_per_minute_wait_meter'), field: 'rate_per_minute_wait_meter', type: 'numeric',  initialEditValue: 0},
  {
    title: t('fleet_admin_comission'), field: 'fleet_admin_fee', type: 'numeric',
     initialEditValue: 0
  },
    { title: t('daily_flat_rate'), field: 'daily_flat_rate', type: 'numeric',  initialEditValue: 0},
    { title: t('extra_info'), field: 'extra_info' , },
  { title: t('position'), field: 'pos', type: 'numeric', defaultSort: 'asc' }
];

export const acceptBid = (selectedBooking, selectedBidder) => {
  return null;
}

export const BidModal = (props) => {
  return null
}

// CSV text already built by a view. Saved like exportUtils.exportCsv saves
// its files: UTF-8 with a byte-order mark, so Excel opens Amharic, Oromo and
// Tigrinya names correctly instead of as garbled letters. New exports should
// call exportCsv({ columns, rows, fileName: datedFileName(...) }) instead,
// which also writes dates in Addis Ababa time on a 12-hour clock.
export const downloadCsv = (data, fileName) => {
  const finalFileName = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
  const text = data === null || data === undefined ? '' : String(data);
  const bom = text.charCodeAt(0) === 0xFEFF ? '' : '\uFEFF';
  const url = URL.createObjectURL(new Blob([bom, text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", finalFileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const generateReceiptHtml = (booking, settings, t, isRTL) => {
  const sealImageUrl = 'https://s3.us-east-1.amazonaws.com/hasettechnology.com/images/partners/seal.png';
  
  // Addis Ababa time on a 12-hour clock ("25 Oct 2026, 2:31 pm").
  const bookingDate = (booking.bookingDate && formatAddisDateTime(booking.bookingDate)) || '-';

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds, 0);
    return date;
  };

  const pickupTime = booking.trip_start_time 
    ? parseTime(booking.trip_start_time)?.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) || '-'
    : '-';
  const dropoffTime = booking.trip_end_time 
    ? parseTime(booking.trip_end_time)?.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) || '-'
    : '-';

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours ? `${hours}h ` : ''}${minutes ? `${minutes}m ` : ''}${secs}s`;
  };

  const formattedTripTime = formatTime(booking.total_trip_time);
  const distance = booking.distance ? booking.distance : 0;
  const distanceUnit = settings?.convert_to_mile ? "mile" : "km";

  // Receipt money math — mirrors mobile-app/src/components/DownloadReceipt.js
  // so the web and mobile PDFs render the same numbers for the same booking.
  //   grossTripCost — headline fare BEFORE discount (booking.trip_cost)
  //   discountNum   — promo discount (booking.discount)
  //   amountPaid    — what the customer actually paid (priority:
  //                   customer_paid set at payment time, else payableAmount
  //                   set at booking, else fall back to trip_cost − discount
  //                   for legacy records). Coerce with parseFloat + `> 0`
  //                   instead of `||` because fields can be either numbers
  //                   or .toFixed() strings — a string "0" is truthy.
  const grossTripCost = parseFloat(booking.trip_cost) || 0;
  const discountNum = parseFloat(booking.discount) || 0;
  const paidNum = parseFloat(booking.customer_paid) || 0;
  const payableNum = parseFloat(booking.payableAmount) || 0;
  const amountPaid = paidNum > 0
    ? paidNum
    : (payableNum > 0 ? payableNum : Math.max(0, grossTripCost - discountNum));
  const dec = (settings && typeof settings.decimal === 'number') ? settings.decimal : 2;
  const fmt = (n) => parseFloat(n || 0).toFixed(dec);

  const waypointsHtml = booking?.waypoints?.map((waypoint, index) => `
    <div class="info">
      <label>${t("marker_title_2")} ${index + 1}</label>
      <span class="text">${waypoint.add}</span>
    </div>
  `).join('') || '';

  const rtlClass = isRTL === 'rtl' ? 'rtl' : '';

  return `
    <!DOCTYPE html>
    <html lang="${(typeof document !== 'undefined' && document.documentElement.getAttribute('lang')) || 'en'}">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${t("ride_receipt")}</title>
        <style>
          body {
            font-family: "Roboto", "Noto Sans Ethiopic", Arial, sans-serif;
            margin: 0;
            padding: 20px;
          }
          .container {
            width: 600px;
            border: 1px solid #ddd;
            border-radius: 5px;
            padding: 20px;
            margin: 0 auto;
            position: relative;
          }
          .header {
            color: #085c91;
            font-size: 24px;
            text-align: center;
            margin-bottom: 10px;
          }
          .subheader {
            color: #333;
            font-size: 18px;
            text-align: center;
            margin-bottom: 10px;
          }
          .text {
            color: #000;
            font-size: 14px;
            line-height: 1.5;
          }
          .bold {
            font-weight: bold;
          }
          .info {
            margin-bottom: 5px;
            display: flex;
            width: 100%;
            justify-content: space-between;
          }
          .info:after {
            display: table;
            clear: both;
            content: "";
          }
          .info label {
            float: left;
            color: #085c91;
            width: 30%;
          }
          .info span {
            width: 70%;
          }
          .separator {
            border-bottom: 2px solid #ddd;
            margin: 15px 0;
          }
          .small-separator {
            border-bottom: 1px solid #ddd;
            margin: 10px 0;
          }
          .seal {
            position: absolute;
            bottom: 20px;
            right: 20px;
            width: 100px;
            height: 100px;
            opacity: 0.8;
          }
          .rtl {
            direction: rtl;
          }
          .rtl .info {
            flex-direction: row-reverse;
          }
          .rtl .info label {
            float: right;
            text-align: right;
          }
          .rtl .seal {
            right: auto;
            left: 20px;
          }
          @media print {
            body { margin: 0; padding: 10px; }
            .container { border: none; box-shadow: none; }
          }
        </style>
      </head>
      <body class="${rtlClass}">
        <div class="container">
          <h1 class="header">${settings?.appName || 'Donkey Drive'}</h1>
          <div class="subheader">${t("ride_receipt")}</div>
          <div class="text">
            ${t("receipt_greeting", { name: booking.customer_name || t("customer"), defaultValue: "Hi {{name}}," })}
          </div>
          <div class="text">
            ${t("ride_greetings")} ${booking.reference || booking.id}
          </div>
          <div class="separator"></div>
          <div class="info">
            <label>${t("date")}:</label>
            <span class="text">${bookingDate}</span>
          </div>
          <div class="bold text">
            <label>${t("total")}:</label>
            ${settings?.symbol || ''}${fmt(amountPaid)}
          </div>
          <div class="separator"></div>
          <div class="text">${t("ride_details")}</div>
          <div class="small-separator"></div>
          <div class="info">
            <label>${t("Gross_trip_cost")}:</label>
            <span class="text">${settings?.symbol || ''}${fmt(grossTripCost)}</span>
          </div>
          ${discountNum > 0 ? `
          <div class="info">
            <label>${t("Discounts")}:</label>
            <span class="text">- ${settings?.symbol || ''}${fmt(discountNum)}</span>
          </div>` : ''}
          <div class="separator"></div>
          <div class="text">${t("payment")}</div>
          <div class="small-separator"></div>
          <div class="info">
            <span class="text bold">${t(booking.payment_mode || 'cash')}</span>
            <span class="text bold">${settings?.symbol || ''}${fmt(amountPaid)}</span>
          </div>
          <div class="separator"></div>
          <div class="text">${t("customer_info", { defaultValue: "Customer Info" })}</div>
          <div class="small-separator"></div>
          <div class="info">
            <label>${t("customer_name")}:</label>
            <span class="text">${booking.customer_name || '-'}</span>
          </div>
          <div class="info">
            <label>${t("customer_contact")}:</label>
            <span class="text">${booking.customer_contact || '-'}</span>
          </div>
          <div class="separator"></div>
          <div class="text">${t("driver_info")}</div>
          <div class="small-separator"></div>
          <div class="info">
            <label>${t("driver_name")}:</label>
            <span class="text">${booking.driver_name || '-'}</span>
          </div>
          <div class="info">
            <label>${t("vehicle_no")}</label>
            <span class="text">${booking.vehicle_number || '-'}</span>
          </div>
          <div class="separator"></div>
          <div class="text">${t("ride_info")}</div>
          <div class="small-separator"></div>
          <div class="info">
            <label>${t("marker_title_1")}:</label>
            <span class="text">${pickupTime} | ${booking.pickupAddress || '-'}</span>
          </div>
          ${waypointsHtml}
          <div class="info">
            <label>${t("marker_title_2")}:</label>
            <span class="text">${dropoffTime} | ${booking.dropAddress || '-'}</span>
          </div>
          <div class="info">
            <label>${t("distance_web")}:</label>
            <span class="text">${distance} ${distanceUnit}</span>
          </div>
          <div class="info">
            <label>${t("ride_time")}:</label>
            <span class="text">${formattedTripTime}</span>
          </div>
          ${Array.isArray(booking.passengers) && booking.passengers.length > 0 ? `
            <div class="separator"></div>
            <div class="text">${t("share_trip_passengers", { defaultValue: "Customers" })} (${booking.passengers.length})</div>
            <div class="small-separator"></div>
            ${booking.passengers.map((p) => `
              <div class="info">
                <label>${(p.firstName || '') + ' ' + (p.lastName || '')} — ${p.phone || '-'}:</label>
                <span class="text">${settings?.symbol || ''}${Number(p.price || 0).toFixed(settings?.decimal || 0)}</span>
              </div>
            `).join('')}
          ` : ''}
          <div class="separator"></div>
          <div class="text bold" style="display: flex; justify-content: center; padding-bottom: 60px;">${t("greetings_text")}</div>
          <img src="${sealImageUrl}" class="seal" alt="Seal" />
        </div>
      </body>
    </html>
  `;
};

// Prints the receipt through a hidden frame on this page (exportUtils), with
// the Ethiopic fonts loaded: no pop-up for the browser to block, and "Save as
// PDF" in the print dialog keeps Amharic names.
export const downloadReceipt = (booking, settings, t, isRTL) => {
  const htmlContent = generateReceiptHtml(booking, settings, t, isRTL);
  return printDocument({ html: htmlContent });
};

export const downloadReceiptAsPdf = async (booking, settings, t, isRTL) => {
  const htmlContent = generateReceiptHtml(booking, settings, t, isRTL);
  return printDocument({ html: htmlContent });
}

export const DeliveryFlow = (props) => {
  return null
}

export const CustomerBidSettings = (props) => {
  return null;
}